import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { 
  LogOut, 
  Home, 
  FileText, 
  Settings, 
  Users, 
  Calendar,
  LayoutDashboard,
  Eye,
  Book,
  ChevronRight,
  ChevronLeft,
  Menu,
  X
} from 'lucide-react';
import { translations } from '@/translations';
import { cn } from '@/lib/utils';

const DashboardLayout: React.FC = () => {
  const { user, signOut } = useAuth();
  const { isAdminOrHigher, isSuper } = useRolePermissions();
  const { language, direction } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  const isRtl = direction === 'rtl';
  
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'לוח בקרה';
    if (path === '/dashboard/pages') return 'ניהול דפים';
    if (path === '/dashboard/yahrzeit') return 'ניהול יארצייט';
    if (path === '/dashboard/users') return 'ניהול משתמשים';
    if (path === '/dashboard/settings') return 'הגדרות';
    return '';
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const menuItems = [
    {
      to: '/dashboard',
      icon: <Home className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'לוח בקרה',
      role: 'viewer',
      bgColor: 'from-blue-500 to-blue-600'
    },
    {
      to: '/dashboard/pages',
      icon: <FileText className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'דפים',
      role: 'member',
      bgColor: 'from-purple-500 to-purple-600'
    },
    {
      to: '/dashboard/yahrzeit',
      icon: <Calendar className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'יארצייט',
      role: 'member',
      bgColor: 'from-orange-500 to-orange-600'
    },
    {
      to: '/dashboard/users',
      icon: <Users className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'משתמשים',
      role: 'admin',
      bgColor: 'from-green-500 to-green-600'
    },
    {
      to: '/admin/dashboard',
      icon: <LayoutDashboard className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'מנהל מערכת',
      role: 'super',
      bgColor: 'from-red-500 to-red-600'
    },
    {
      to: '/dashboard/settings',
      icon: <Settings className={`${isRtl ? 'ml-2' : 'mr-2'}`} />,
      label: 'הגדרות',
      role: 'member',
      bgColor: 'from-gray-500 to-gray-600'
    },
  ];

  return (
    <div className={cn("flex h-screen bg-gray-50", isRtl ? 'flex-row-reverse' : '')}>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="rounded-full shadow-lg bg-white"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
      </div>

      {/* Sidebar - desktop */}
      <aside 
        className={cn(
          "bg-gradient-to-b from-synagogue-blue to-blue-900 text-white transition-all duration-300 ease-in-out hidden lg:block",
          sidebarCollapsed ? "w-20" : "w-64"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-blue-800 flex items-center justify-between">
            {!sidebarCollapsed && (
              <div>
                <h2 className="text-xl font-bold text-white">לוח הודעות</h2>
                <p className="text-sm text-blue-200 truncate">{user?.email}</p>
              </div>
            )}
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleSidebar}
              className="text-white hover:bg-blue-800 rounded-full"
            >
              {isRtl ? 
                (sidebarCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />) : 
                (sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />)
              }
            </Button>
          </div>
          
          {/* Navigation */}
          <nav className="p-4 space-y-2 flex-1 overflow-y-auto custom-scrollbar">
            {menuItems.map((item) => {
              // Check if the user has permission to see this item
              let hasAccess = true;
              if (item.role === 'admin' && !isAdminOrHigher) hasAccess = false;
              if (item.role === 'super' && !isSuper) hasAccess = false;
              
              if (!hasAccess) return null;
              
              return (
                <NavLink 
                  key={item.to}
                  to={item.to} 
                  className={({ isActive }) => cn(
                    "flex items-center p-3 rounded-lg transition-all duration-200",
                    isRtl ? "flex-row-reverse" : "",
                    sidebarCollapsed ? "justify-center" : "",
                    isActive ? 
                      `bg-gradient-to-r ${item.bgColor} shadow-md` : 
                      "hover:bg-blue-800/50"
                  )}
                >
                  <div className={cn(
                    "flex items-center",
                    sidebarCollapsed ? "justify-center" : "w-full",
                    isRtl ? "flex-row-reverse" : ""
                  )}>
                    {item.icon}
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </div>
                </NavLink>
              );
            })}
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-blue-800">
            <Button 
              variant="ghost"
              onClick={handleLogout}
              className={cn(
                "w-full text-white hover:bg-blue-800 transition-all duration-200",
                isRtl ? "flex-row-reverse" : "",
                sidebarCollapsed ? "justify-center" : ""
              )}
            >
              <LogOut className={`h-5 w-5 ${isRtl ? 'ml-2' : 'mr-2'}`} />
              {!sidebarCollapsed && "התנתק"}
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/display')}
              className={cn(
                "w-full mt-2 bg-white/10 text-white hover:bg-white/20 border-white/20 transition-all duration-200",
                isRtl ? "flex-row-reverse" : "",
                sidebarCollapsed ? "justify-center" : ""
              )}
            >
              <Eye className={`h-5 w-5 ${isRtl ? 'ml-2' : 'mr-2'}`} />
              {!sidebarCollapsed && "צפה בתצוגה"}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileMenuOpen && (
        <aside className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
          <div className={cn(
            "absolute top-0 bottom-0 w-64 bg-gradient-to-b from-synagogue-blue to-blue-900 text-white",
            isRtl ? "right-0" : "left-0"
          )}>
            <div className="p-4 border-b border-blue-800">
              <h2 className="text-xl font-bold text-white">לוח הודעות</h2>
              <p className="text-sm text-blue-200 truncate">{user?.email}</p>
            </div>
            
            <nav className="p-4 space-y-2">
              {menuItems.map((item) => {
                // Check if the user has permission to see this item
                let hasAccess = true;
                if (item.role === 'admin' && !isAdminOrHigher) hasAccess = false;
                if (item.role === 'super' && !isSuper) hasAccess = false;
                
                if (!hasAccess) return null;
                
                return (
                  <NavLink 
                    key={item.to}
                    to={item.to} 
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => cn(
                      "flex items-center p-3 rounded-lg transition-all duration-200",
                      isRtl ? "flex-row-reverse" : "",
                      isActive ? 
                        `bg-gradient-to-r ${item.bgColor} shadow-md` : 
                        "hover:bg-blue-800/50"
                    )}
                  >
                    <div className={cn(
                      "flex items-center w-full",
                      isRtl ? "flex-row-reverse" : ""
                    )}>
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                  </NavLink>
                );
              })}
            </nav>
            
            <div className="p-4 border-t border-blue-800 absolute bottom-0 w-full">
              <Button 
                variant="ghost"
                onClick={handleLogout}
                className={cn(
                  "w-full text-white hover:bg-blue-800 transition-all duration-200",
                  isRtl ? "flex-row-reverse" : ""
                )}
              >
                <LogOut className={`h-5 w-5 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                התנתק
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => {
                  navigate('/display');
                  setMobileMenuOpen(false);
                }}
                className={cn(
                  "w-full mt-2 bg-white/10 text-white hover:bg-white/20 border-white/20 transition-all duration-200",
                  isRtl ? "flex-row-reverse" : ""
                )}
              >
                <Eye className={`h-5 w-5 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                צפה בתצוגה
              </Button>
            </div>
          </div>
        </aside>
      )}
      
      {/* Main content */}
      <main className="flex-1 overflow-auto bg-gray-50">
        <header className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-synagogue-blue">{getPageTitle()}</h1>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/display')}
                className="hidden sm:flex"
              >
                <Eye className={`h-4 w-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                צפה בתצוגה
              </Button>
            </div>
          </div>
        </header>
        
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout; 